import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { get, post, patch, _delete } from '../services/api';
import Loading from './Loading/Loading';
import colors from '../utils/colors';

const MAX_WIDTH = 800;

export default function News() {
  const { id } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(id !== 'new');
  const [formData, setFormData] = useState({
    title: '',
    text: '',
    mediaUrl: '',
    link: '',
    hasPoll: false,
    pollQuestion: '',
    pollOptions: ['', ''], // Start with 2 options
    pollExpiresAt: '',
    pollResults: [],
  });

  useEffect(() => {
    if (id !== 'new') {
        const fetchNews = async () => {
          const data = await get(`/admin/news/${id}`);
          if (data) {
            setFormData({
              title: data.title || '',
              text: data.text || '',
              mediaUrl: data.mediaUrl || '',
              link: data.link || '',
              hasPoll: !!data.poll,
              pollQuestion: data.poll?.question || '',
              pollOptions: data.poll?.pollOptions?.map(o => o.text) || ['', ''],
              pollResults: data.poll?.pollOptions?.map(o => ({ text: o.text, votes: o._count?.pollVotes || 0 })) || [],
              pollExpiresAt: data.poll?.expiresAt ? data.poll.expiresAt.split('T')[0] : '',
            });
          }
          setLoading(false);
        };
        fetchNews();
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.pollOptions];
    newOptions[index] = value;
    setFormData(prev => ({ ...prev, pollOptions: newOptions }));
  };

  const addOption = () => {
    setFormData(prev => ({ ...prev, pollOptions: [...prev.pollOptions, ''] }));
  };

  const removeOption = (index) => {
    const newOptions = formData.pollOptions.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, pollOptions: newOptions }));
  };

  const handleSave = async () => {
    const payload = {
        title: formData.title,
        text: formData.text,
        mediaUrl: formData.mediaUrl,
        link: formData.link,
    };

    if (formData.hasPoll) {
        payload.poll = {
            question: formData.pollQuestion,
            options: formData.pollOptions.filter(o => o.trim() !== ''),
            expiresAt: formData.pollExpiresAt ? new Date(formData.pollExpiresAt).toISOString() : null,
        };
    }

    let res;
    if (id === 'new') {
        res = await post(`/admin/news`, payload);
    } else {
        res = await patch(`/admin/news/${id}`, payload);
    }

    if (res) {
        history.push('/news');
    } else {
        alert('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async () => {
      if (window.confirm('Voulez-vous vraiment supprimer cette actualité ?')) {
          await _delete(`/admin/news/${id}`);
          history.push('/news');
      }
  }

  if (loading) return <Loading />;

  return (
    <div style={{ padding: 20, maxWidth: MAX_WIDTH, margin: '0 auto' }}>
      <h2>{id === 'new' ? 'Nouvelle Actualité' : 'Modifier Actualité'}</h2>

      <div style={styles.inputGroup}>
        <label style={styles.label}>Titre</label>
        <input 
          style={styles.input} 
          name="title" 
          value={formData.title} 
          onChange={handleChange} 
          placeholder="Titre de l'actualité"
        />
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>Texte</label>
        <textarea 
          style={{...styles.input, height: 100, fontFamily: 'inherit'}} 
          name="text" 
          value={formData.text} 
          onChange={handleChange} 
          placeholder="Contenu de l'actualité"
        />
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>URL Média (Image/Video)</label>
        <input 
          style={styles.input} 
          name="mediaUrl" 
          value={formData.mediaUrl} 
          onChange={handleChange} 
          placeholder="https://..."
        />
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>Lien externe (CTA)</label>
        <input 
          style={styles.input} 
          name="link" 
          value={formData.link} 
          onChange={handleChange} 
          placeholder="https://..."
        />
      </div>

      <div style={{ marginTop: 30, marginBottom: 20, borderTop: '1px solid #ddd', paddingTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <input 
                type="checkbox" 
                name="hasPoll" 
                checked={formData.hasPoll} 
                onChange={handleChange} 
                style={{ width: 20, height: 20, marginRight: 10 }}
            />
            <label style={{ fontSize: '1.2em', fontWeight: 'bold' }}>Ajouter un sondage</label>
        </div>

        {formData.hasPoll && (
            <div style={{ marginLeft: 30, marginTop: 15, padding: 15, backgroundColor: '#f9f9f9', borderRadius: 5 }}>
                <div style={styles.inputGroup}>
                    <label style={styles.label}>Question</label>
                    <input 
                        style={styles.input} 
                        name="pollQuestion" 
                        value={formData.pollQuestion} 
                        onChange={handleChange} 
                        placeholder="Posez votre question..."
                    />
                </div>

                <label style={styles.label}>Options de réponse</label>
                {formData.pollOptions.map((opt, index) => (
                    <div key={index} style={{ display: 'flex', marginBottom: 10 }}>
                        <input 
                            style={{ ...styles.input, flex: 1 }}
                            value={opt} 
                            onChange={(e) => handleOptionChange(index, e.target.value)} 
                            placeholder={`Option ${index + 1}`}
                        />
                        {formData.pollOptions.length > 2 && (
                             <button onClick={() => removeOption(index)} style={styles.removeBtn}>X</button>
                        )}
                    </div>
                ))}
                <button onClick={addOption} style={styles.secondaryBtn}>+ Ajouter une option</button>

                <div style={{...styles.inputGroup, marginTop: 20}}>
                    <label style={styles.label}>Date d'expiration (Optionnel)</label>
                    <input 
                        type="date" 
                        name="pollExpiresAt" 
                        value={formData.pollExpiresAt} 
                        onChange={handleChange} 
                        style={styles.input} 
                    />
                </div>

                {id !== 'new' && formData.pollResults && formData.pollResults.length > 0 && (
                    <div style={{ marginTop: 20, padding: 15, backgroundColor: '#eef', borderRadius: 5 }}>
                        <h4 style={{ margin: '0 0 10px 0' }}>Résultats actuels ({formData.pollResults.reduce((sum, r) => sum + r.votes, 0)} votes)</h4>
                        {formData.pollResults.map((res, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                                <span>{res.text}</span>
                                <strong>{res.votes} votes</strong>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 40 }}>
        {id !== 'new' ? (
             <button onClick={handleDelete} style={styles.deleteBtn}>Supprimer</button>
        ) : <div />}
        
        <button onClick={handleSave} style={styles.primaryBtn}>
            Enregistrer
        </button>
      </div>
    </div>
  );
}

const styles = {
  inputGroup: {
    marginBottom: 15,
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    marginBottom: 5,
    fontWeight: 'bold',
    color: '#555',
  },
  input: {
    padding: 10,
    fontSize: '1em',
    borderRadius: 5,
    border: '1px solid #ccc',
    outline: 'none',
  },
  primaryBtn: {
    backgroundColor: colors.dark_blue,
    color: 'white',
    padding: '12px 25px',
    border: 'none',
    borderRadius: 5,
    fontSize: '1.1em',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  secondaryBtn: {
    backgroundColor: '#eee',
    color: '#333',
    padding: '8px 15px',
    border: 'none',
    borderRadius: 5,
    cursor: 'pointer',
    fontSize: '0.9em',
  },
  deleteBtn: {
    backgroundColor: 'red',
    color: 'white',
    padding: '12px 25px',
    border: 'none',
    borderRadius: 5,
    fontSize: '1.1em',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  removeBtn: {
      marginLeft: 10,
      backgroundColor: 'transparent',
      border: '1px solid red',
      color: 'red',
      borderRadius: 5,
      cursor: 'pointer',
      padding: '0 10px'
  }
};
